import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../../components/Layout';
import { apiFetch } from '../../lib/api';
import { useFiltroSocieta } from '../../hooks/useFiltroSocieta';
import { Posizione, StatoPosizione, StatoCandidatura } from '../../types';
import '../../styles/ats.css';

const STATO_BADGE: Record<StatoPosizione, string> = {
  APERTA:   'badge-green',
  CHIUSA:   'badge-slate',
  SOSPESA:  'badge-yellow',
};

const CANDIDATURA_LABEL: Partial<Record<StatoCandidatura, string>> = {
  RICEVUTO:     'Ricevuti',
  SCREENING:    'Screening',
  COLLOQUIO_1:  'Coll. 1',
  COLLOQUIO_2:  'Coll. 2',
  OFFERTA:      'Offerta',
  ASSUNTO:      'Assunti',
  RIFIUTATO:    'Rifiutati',
};

const STATI_ORDINE: StatoCandidatura[] = [
  'RICEVUTO','SCREENING','COLLOQUIO_1','COLLOQUIO_2','OFFERTA','ASSUNTO','RIFIUTATO',
];

export default function ListaPosizioni() {
  const navigate = useNavigate();
  const [posizioni,  setPosizioni]  = useState<Posizione[]>([]);
  const [loading,    setLoading]    = useState(true);
  const [error,      setError]      = useState('');

  const {
    societaId: fSocieta,
    setSocietaId: setFSocieta,
    listaSocieta: societa,
    isUnica,
    pronta,
  } = useFiltroSocieta();

  const [fStato,    setFStato]    = useState('APERTA');

  /* Modale nuova posizione */
  const [showModal,     setShowModal]     = useState(false);
  const [saving,        setSaving]        = useState(false);
  const [modalError,    setModalError]    = useState('');
  const [form, setForm] = useState({
    societaId:   '',
    titolo:      '',
    descrizione: '',
    requisiti:   '',
  });

  const load = async () => {
    if (!pronta) return;
    const params = new URLSearchParams();
    if (fSocieta) params.set('societaId', fSocieta);
    if (fStato)   params.set('stato',     fStato);
    const data = await apiFetch<{ posizioni: Posizione[] }>(`/api/ats/posizioni?${params}`);
    setPosizioni(data.posizioni);
  };

  // Auto-fill societaId in form when single society
  useEffect(() => {
    if (isUnica && societa.length === 1) setForm((f) => ({ ...f, societaId: String(societa[0].id) }));
  }, [isUnica, societa]);

  useEffect(() => {
    setLoading(true);
    load().catch((e) => setError(e.message)).finally(() => setLoading(false));
  }, [pronta, fSocieta, fStato]); // eslint-disable-line

  const openModal = () => {
    setModalError('');
    setForm((f) => ({ ...f, titolo: '', descrizione: '', requisiti: '' }));
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!form.societaId || !form.titolo.trim()) {
      setModalError('Società e titolo sono obbligatori.');
      return;
    }
    setSaving(true);
    setModalError('');
    try {
      const created = await apiFetch<{ posizione: Posizione }>('/api/ats/posizioni', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          societaId:   Number(form.societaId),
          titolo:      form.titolo.trim(),
          descrizione: form.descrizione.trim() || null,
          requisiti:   form.requisiti.trim()   || null,
        }),
      });
      setShowModal(false);
      navigate(`/ats/posizioni/${created.posizione.id}`);
    } catch (e: any) {
      setModalError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const fmtDate = (d?: string | null) => d ? new Date(d).toLocaleDateString('it-IT') : '—';

  return (
    <Layout title="Selezione — Posizioni">
      {/* Toolbar */}
      <div className="ats-toolbar">
        {!isUnica && (
          <select className="bop-select bop-select-inline--md" value={fSocieta} onChange={(e) => setFSocieta(e.target.value)}>
            <option value="">Tutte le società</option>
            {societa.map((s) => <option key={s.id} value={s.id}>{s.nome}</option>)}
          </select>
        )}
        <select className="bop-select bop-select-inline--sm" value={fStato} onChange={(e) => setFStato(e.target.value)}>
          <option value="">Tutte le posizioni</option>
          <option value="APERTA">Aperte</option>
          <option value="SOSPESA">Sospese</option>
          <option value="CHIUSA">Chiuse</option>
        </select>
        <button className="btn btn-primary ats-toolbar-spacer" onClick={openModal}>
          + Nuova Posizione
        </button>
      </div>

      {error && <div className="inline-error">{error}</div>}

      {loading ? (
        <div className="inline-loading">Caricamento…</div>
      ) : posizioni.length === 0 ? (
        <div className="bop-empty-state">Nessuna posizione trovata.</div>
      ) : (
        <div className="ats-posizioni-grid">
          {posizioni.map((p) => {
            const tot = Object.values(p.contatori ?? {}).reduce((a, b) => a + (b ?? 0), 0);
            return (
              <div
                key={p.id}
                className="card ats-posizione-card"
                onClick={() => navigate(`/ats/posizioni/${p.id}`)}
              >
                <div className="ats-posizione-header">
                  <strong className="ats-posizione-title">{p.titolo}</strong>
                  <span className={`badge ${STATO_BADGE[p.stato]} ats-posizione-badge`}>{p.stato}</span>
                </div>
                {p.societaNome && (
                  <div className="ats-posizione-societa">{p.societaNome}</div>
                )}
                <div className="ats-posizione-meta">
                  Aperta il {fmtDate(p.dataApertura)}
                  {p.dataChiusura && ` · Chiusa il ${fmtDate(p.dataChiusura)}`}
                </div>

                {/* Contatori per stato */}
                <div className="ats-posizione-contatori">
                  {STATI_ORDINE.map((stato) => {
                    const n = p.contatori?.[stato] ?? 0;
                    if (n === 0) return null;
                    return (
                      <span key={stato} className="ats-contatore-tag">
                        {CANDIDATURA_LABEL[stato]}: <strong>{n}</strong>
                      </span>
                    );
                  })}
                  {tot === 0 && <span className="ats-posizione-empty">Nessun candidato</span>}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Modale nuova posizione */}
      {showModal && (
        <div className="modal-backdrop" onClick={() => !saving && setShowModal(false)}>
          <div className="modal ats-modal-width-500" onClick={(e) => e.stopPropagation()}>
            <h3 className="ats-modal-title">Nuova Posizione</h3>

            {modalError && <div className="inline-error ats-modal-error">{modalError}</div>}

            {!isUnica && (
              <div className="ats-modal-field">
                <label className="form-label">Società *</label>
                <select className="bop-select" value={form.societaId} onChange={(e) => setForm({ ...form, societaId: e.target.value })}>
                  <option value="">Seleziona…</option>
                  {societa.map((s) => <option key={s.id} value={s.id}>{s.nome}</option>)}
                </select>
              </div>
            )}

            <div className="ats-modal-field">
              <label className="form-label">Titolo posizione *</label>
              <input className="bop-input" value={form.titolo} onChange={(e) => setForm({ ...form, titolo: e.target.value })} placeholder="es. Backend Developer" />
            </div>

            <div className="ats-modal-field">
              <label className="form-label">Descrizione</label>
              <textarea className="bop-input" rows={3} value={form.descrizione} onChange={(e) => setForm({ ...form, descrizione: e.target.value })} placeholder="Descrizione del ruolo…" />
            </div>

            <div className="ats-modal-note-field">
              <label className="form-label">Requisiti</label>
              <textarea className="bop-input" rows={3} value={form.requisiti} onChange={(e) => setForm({ ...form, requisiti: e.target.value })} placeholder="Requisiti richiesti…" />
            </div>

            <div className="ats-modal-actions">
              <button className="btn btn-ghost" onClick={() => setShowModal(false)} disabled={saving}>Annulla</button>
              <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
                {saving ? 'Salvataggio…' : 'Crea Posizione'}
              </button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}
